import { mapState } from "vuex";

/**
 * Which of a user's fields Whistle may still edit once a client feed owns the record.
 *
 * `UserClients.informManaged` used to answer this on its own: true locked every field.
 * That was right while the feed won everything, and stopped being right when the merge
 * gained an override list — the graph has decided per property since ADO Feature 186742
 * "Engage-Inform User Synchronization and Merged User Creation", and the UI was still
 * answering per record.
 *
 * **The rule here mirrors `UserMergeService.Merge` rather than restating it.** That
 * merge lays the client's renamed properties down first, then lets the Engage side
 * write a key only when the key is absent or named in `allowUserOverrideProperties`.
 * So Engage wins a property when:
 *
 *   - no client property is mapped onto it — there is no collision to lose, or
 *   - one is, and the property is in the override list.
 *
 * Anything else belongs to the feed, and an edit to it in Whistle would be reverted by
 * the next nightly pass with nothing to show for it. Locking exactly that set is the
 * honest boundary: every field this leaves editable is one whose value survives.
 *
 * Deriving it from the tenant's own configuration rather than a list in code is what
 * keeps the two in step — unmap a property or add an override and the UI follows with
 * no release.
 */
export default {
  computed: {
    ...mapState([
      "engageConfiguration",
      "engageConfigurationClientId",
      "engageConfigurationLoading",
    ]),
    /**
     * Engage property names a client property is renamed onto.
     *
     * `ExternalPropertyMap` is stored as `{ clientProperty: engageProperty }` — keyed
     * by the client's name, because that is the direction the merge looks a rename up
     * in. The Engage names are therefore its VALUES.
     */
    feedSuppliedProperties() {
      return new Set(Object.values(this.engageConfiguration?.ExternalPropertyMap ?? {}));
    },
    /** Engage property names that win a collision. */
    engageOverrideProperties() {
      return new Set(this.engageConfiguration?.AllowUserOverrideProperties ?? []);
    },
    /**
     * Whether this tenant's users go through the merge at all.
     *
     * Mirrors `UserMergeService.ParticipatesInUserMerge`: EITHER label opts a tenant in.
     * Not the external label alone — a tenant with only an internal label is one Engage
     * owns outright, which merges and whose fields are all Engage's to edit.
     *
     * Cannot be a null check, which is the trap here. An unconfigured tenant answers
     * `200` with an EMPTY DOCUMENT rather than `404` — "not configured" is a normal
     * steady state upstream, not a missing resource — so `engageConfiguration` is a
     * truthy `{}` for every client that has never opted in. Treating that as configured
     * reports nothing feed-owned, which unlocks every field for exactly those clients.
     */
    engageMergeConfigured() {
      const configuration = this.engageConfiguration;
      if (!configuration) return false;
      return Boolean(configuration.InternalUserLabel || configuration.ExternalUserLabel);
    },
  },
  methods: {
    /**
     * Whether Engage's value for this property survives the merge.
     *
     * @param {string} engageProperty An Engage user property name, e.g. "businessPhone".
     * @returns {boolean}
     */
    isEngageOwnedProperty(engageProperty) {
      if (!engageProperty) return false;
      // No configuration means this tenant has not moved onto the merge — it is on the
      // pull that predates the override list, where the feed wins everything and
      // `informManaged` locking the whole record was the correct answer. Absent
      // configuration must read as "feed owns it", never as an empty map: an empty map
      // says nothing is mapped, which would unlock every field for exactly the clients
      // that have not opted in.
      if (!this.engageMergeConfigured) return false;
      if (!this.feedSuppliedProperties.has(engageProperty)) return true;
      return this.engageOverrideProperties.has(engageProperty);
    },
    /**
     * Whether a field must be read-only for an Inform-managed user.
     *
     * A property this does not recognise reads as feed-owned. The caller has not said
     * which Engage property the input writes, and guessing in the permissive direction
     * would unlock a field whose edits the sync may discard — the failure this exists
     * to prevent.
     *
     * @param {string} engageProperty
     * @returns {boolean}
     */
    isManagedFieldReadonly(engageProperty) {
      return !this.isEngageOwnedProperty(engageProperty);
    },
    /**
     * Load the configuration this rule reads. Cached per client in the store, so a
     * component may call it on every mount.
     * @param {number|string} [clientId]
     * @returns {Promise<void>}
     */
    async loadEngageConfiguration(clientId) {
      await this.$store.dispatch("fetchEngageConfiguration", { clientId });
    },
  },
};
